import {
  InstrumentSummaryDetailed,
  useInstrumentInsight,
} from "@iliotech/watchlist-api";
import { Toggle } from "../../../../../components/Toggle/Toggle";
import React, { useState } from "react";
import s from "./InstrumentPriceChart.module.scss";
import TimeSeriesLineChart from "@widgets/components/TimeSeriesLineChart/TimeSeriesLineChart";
import { TimeSeriesChartItem } from "@widgets/main";
import classNames from "classnames";
import { getPerfVsBenchmarkData } from "@widgets/insights/InstrumentLevel/__processing";
import MultiLineTimeSeriesChart from "@widgets/components/MultiLineTimeSeriesChart/MultiLineTimeSeriesChart";
import { useSmallScreen } from "@widgets/utils";
import { ChartSeriesItemProps } from "@progress/kendo-react-charts";
import { IconsToggle } from "../IconsToggle/IconsToggle";
import { LineChartIcon } from "./assets/LineChartIcon";
import { CandlestickChartIcon } from "./assets/CandlestickChartIcon";

interface IProps {
  instrumentSummaryDetailed: InstrumentSummaryDetailed;
  categoryAxisFormatter?: (date?: string) => string;
  tooltipCategoryAxisFormatter?: (date?: string) => string;
  selectedMode: "price" | "relativePricePerformance";
  setSelectedMode: (mode: "price" | "relativePricePerformance") => void;
  chartType: string;
  setChartType: (type: string) => void;
  watchlistId?: string;
}

const PRICE_CHART_TOGGLES: {
  label: string;
  value: PriceChartToggleValue;
  color: string;
}[] = [
  { label: "Close Price", value: "closepx_abs", color: "var(--price-color)" },
  {
    label: "Target Price",
    value: "targetprice_abs",
    color: "rgb(175, 10, 175)",
  },
  { label: "20d SMA", value: "sma_20d_abs", color: "rgba(255, 165, 0, 1)" },
  { label: "50d SMA", value: "sma_50d_abs", color: "rgba(0, 0, 255, 1)" },
  { label: "100d SMA", value: "sma_100d_abs", color: "rgba(0, 128, 0, 1)" },
  { label: "200d SMA", value: "sma_200d_abs", color: "rgba(255, 0, 0, 1)" },
  {
    label: "2σ Bollinger Bands",
    value: "bollinger_2std",
    color: "rgba(64, 224, 208, 0.3)",
  },
  {
    label: "3σ Bollinger Bands",
    value: "bollinger_3std",
    color: "rgba(255, 223, 128, 0.3)",
  },
];

type PriceChartToggleValue =
  | "closepx_abs"
  | "targetprice_abs"
  | "sma_20d_abs"
  | "sma_50d_abs"
  | "sma_100d_abs"
  | "sma_200d_abs"
  | "bollinger_2std"
  | "bollinger_3std";

// Extend the TimeSeriesChartItem interface to include min and max for area series
interface ExtendedTimeSeriesChartItem extends TimeSeriesChartItem {
  entries: {
    date: string | Date;
    value?: number;
    open?: number;
    high?: number;
    low?: number;
    close?: number;
    min?: number;
    max?: number;
  }[];
  line?: {
    color: string;
    width: number;
  };
}

export const InstrumentPriceChart = ({
  instrumentSummaryDetailed,
  categoryAxisFormatter,
  tooltipCategoryAxisFormatter,
  selectedMode,
  setSelectedMode,
  chartType,
  setChartType,
  watchlistId,
}: IProps) => {
  const { instrumentTimeseries } = instrumentSummaryDetailed || [];
  const smallScreen = useSmallScreen();
  const { data } = useInstrumentInsight(
    instrumentSummaryDetailed.instrument.id,
    "PERFORMANCE_VS_BENCHMARK",
    watchlistId
  );
  const { chartData, minValue } = getPerfVsBenchmarkData({
    instrument: instrumentSummaryDetailed.instrument,
    ...(data?.chart?.data as any),
  });

  const [selectedToggle, setSelectedToggle] = useState<PriceChartToggleValue[]>(
    ["closepx_abs"]
  );

  const isPortfolio = instrumentSummaryDetailed.instrument.type === "Portfolio";

  const {
    priceChartItems,
    minPriceChartItem,
  }: {
    priceChartItems: ExtendedTimeSeriesChartItem[];
    minPriceChartItem: number;
  } = React.useMemo(() => {
    // Create base data array
    const chartData: ExtendedTimeSeriesChartItem[] = [];

    // Add candlestick data if chartType is candlestick
    if (chartType === "candlestick") {
      chartData.push({
        category: isPortfolio
          ? instrumentSummaryDetailed.instrument.name
          : instrumentSummaryDetailed.instrument.sourceId,
        color: "var(--good)",
        seriesType: "candlestick",
        entries: instrumentTimeseries.map((item) => ({
          date: new Date(item.date!),
          open: item.open || item.closepx_abs!,
          high: item.high || item.closepx_abs!,
          low: item.low || item.closepx_abs!,
          close: item.closepx_abs!,
          value: item.closepx_abs!,
        })),
      });
    }

    // Add all selected toggles (except closepx_abs if we're in candlestick mode and except 2σ Bollinger)
    selectedToggle.forEach((toggle) => {
      // Skip closepx_abs in candlestick mode as it's already included in the candlestick series
      if (chartType === "candlestick" && toggle === "closepx_abs") {
        return;
      }

      // Skip 2σ Bollinger as we'll add it last
      if (toggle === "bollinger_2std") {
        return;
      }

      // Handle 3σ Bollinger Bands
      if (toggle === "bollinger_3std") {
        chartData.push({
          category: "3σ Bollinger Bands",
          color: "rgba(255, 223, 128, 1)",
          seriesType: "rangeArea",
          entries: instrumentTimeseries.map((item) => ({
            date: new Date(item.date!),
            min: item.lowerbollingerband3std_20d_abs!,
            max: item.upperbollingerband3std_20d_abs!,
            value: item.closepx_abs!, // For tooltip display
          })),
        });
        return;
      }

      // Handle regular line series
      chartData.push({
        category:
          toggle === "closepx_abs"
            ? isPortfolio
              ? instrumentSummaryDetailed.instrument.name
              : instrumentSummaryDetailed.instrument.sourceId
            : PRICE_CHART_TOGGLES.find((t) => t.value === toggle)?.label!,
        color: PRICE_CHART_TOGGLES.find((t) => t.value === toggle)?.color,
        entries: instrumentTimeseries.map((item) => ({
          date: new Date(item.date!),
          value: item[toggle]!,
        })),
        dashType: toggle === "closepx_abs" ? "solid" : "dash",
        lineWidth: toggle === "closepx_abs" ? 2 : 2.5,
        seriesType: "line",
      });
    });

    // Add 2σ Bollinger Bands last if selected
    if (selectedToggle.includes("bollinger_2std")) {
      chartData.push({
        category: "2σ Bollinger Bands",
        color: "rgba(64, 224, 208, 1)",
        seriesType: "rangeArea",
        entries: instrumentTimeseries.map((item) => ({
          date: new Date(item.date!),
          min: item.lowerbollingerband2std_20d_abs!,
          max: item.upperbollingerband2std_20d_abs!,
          value: item.closepx_abs!, // For tooltip display
        })),
      });
    }

    // Calculate minPriceChartItem
    const minPriceChartItem =
      chartData.reduce((min, item) => {
        const itemMin = item.entries
          .filter((entry) => {
            // For candlestick series, check for low value
            if (item.seriesType === "candlestick") {
              return !!entry?.low;
            }
            // For rangeArea series (Bollinger Bands), check for min value
            if (item.seriesType === "rangeArea") {
              return !!entry?.min;
            }
            // For other series, check for value
            return !!entry?.value;
          })
          .reduce((min, entry) => {
            // For candlestick series, use low value
            if (item.seriesType === "candlestick") {
              return Math.min(min, entry.low!);
            }
            // For rangeArea series (Bollinger Bands), use min value
            if (item.seriesType === "rangeArea") {
              return Math.min(min, entry.min!);
            }
            // For other series, use value
            return Math.min(min, entry.value!);
          }, Infinity);
        return Math.min(min, itemMin);
      }, Infinity) * 0.9;

    return {
      priceChartItems: chartData,
      minPriceChartItem,
    };
  }, [
    instrumentTimeseries,
    selectedToggle,
    chartType,
    isPortfolio,
    instrumentSummaryDetailed.instrument,
  ]);

  const handleToggleChange = (toggle: PriceChartToggleValue) => {
    if (selectedToggle.includes(toggle)) {
      setSelectedToggle((prev) => prev.filter((t) => t !== toggle));
    } else {
      setSelectedToggle((prev) => [...prev, toggle]);
    }
  };

  const priceValueFormatter = (value: number) => `${value?.toFixed(2)}`;

  const renderInner = React.useMemo(() => {
    if (selectedMode === "price") {
      const chartItemSeriesProps: ChartSeriesItemProps = {
        highField: "high",
        lowField: "low",
        openField: "open",
        closeField: "close",
        downColor: "var(--bad)",
        fromField: "min",
        toField: "max",
        line: {
          color: "black",
        },
      };
      return (
        <div>
          {selectedMode === "price" && (
            <div className={s.chartTypeToggle}>
              <IconsToggle
                value={chartType}
                setValue={(val) => setChartType(val)}
                options={[
                  { label: "Line", value: "line", icon: <LineChartIcon /> },
                  {
                    label: "Candlestick",
                    value: "candlestick",
                    icon: <CandlestickChartIcon />,
                  },
                ]}
              />
            </div>
          )}
          <TimeSeriesLineChart
            maxDivisions={smallScreen ? 4 : 12}
            items={priceChartItems}
            valueAxisFormatter={priceValueFormatter}
            categoryAxisFormatter={categoryAxisFormatter}
            tooltipCategoryAxisFormatter={tooltipCategoryAxisFormatter}
            seriesType={chartType === "candlestick" ? "candlestick" : "line"}
            chartSeriesItemProps={chartItemSeriesProps}
          />
          <div
            style={{
              marginTop: "10px",
              marginBottom: "10px",
              display: "flex",
              flexDirection: "row",
              gap: "10px",
            }}
            className={s.toggleContainer}
          >
            {PRICE_CHART_TOGGLES.map((toggle) => (
              <span
                key={toggle.value}
                className={classNames(
                  s.toggle,
                  selectedToggle.includes(toggle.value) && s.active,
                  (toggle.value === "bollinger_2std" ||
                    toggle.value === "bollinger_3std") &&
                    s.bollingerToggle
                )}
                style={{
                  backgroundColor: selectedToggle.includes(toggle.value)
                    ? toggle.color
                    : "transparent",
                }}
                onClick={() => handleToggleChange(toggle.value)}
              >
                <div className={s.toggleLabel} style={{}}>
                  {toggle.label === "Close Price"
                    ? isPortfolio
                      ? instrumentSummaryDetailed.instrument.name
                      : instrumentSummaryDetailed.instrument.sourceId
                    : toggle.label}
                </div>
              </span>
            ))}
          </div>
        </div>
      );
    }
    const valueFormatter = (value: number) => `${value?.toFixed(2)}%`;
    return (
      <MultiLineTimeSeriesChart
        lineStyle={"smooth"}
        yAxisTitle={"Return"}
        items={chartData || []}
        baseUnit={"days"}
        axisCrossingValue={minValue}
        valueAxisFormatter={valueFormatter}
        categoryAxisFormatter={categoryAxisFormatter}
        valueAxisNotes={{
          data: [{ value: 0 }],
          line: { width: 2, color: "#E0E0E0", length: 2000, dashType: "dash" },
        }}
        tooltipSettings={{
          showZeroes: true,
        }}
        min={minValue}
      />
    );
  }, [selectedMode, priceChartItems, selectedToggle, smallScreen, chartType]);
  return (
    <div>
      <div className={s.row}>
        <h2 className={"element-title"}>Price</h2>

        <Toggle
          position={selectedMode === "price" ? "LEFT" : "RIGHT"}
          left={"Price"}
          right={"Relative Price Performance"}
          onClick={(val) => {
            setSelectedMode(
              val === "LEFT" ? "price" : "relativePricePerformance"
            );
          }}
        />
      </div>

      {renderInner}
    </div>
  );
};
